<template>
  <article class="fold-out-block">
    <div
      v-for="(item, index) in state.blocks"
      :key="index"
      class="fold-out-block-item"
      :class="{ open: state.openItems.includes(Number(index)) }"
    >
      <div
        class="fold-out-block-item-header"
        :class="state.openItems.includes(Number(index)) && 'is--open'"
        @click="toggleItem(Number(index))"
        @keydown.space.prevent="toggleItem(Number(index))"
        @keydown.enter.prevent="toggleItem(Number(index))"
      >
        <Heading
          :id="'heading-' + index"
          :content="item.title"
          :level="3"
          class="fold-out-block-item-heading"
        />
        <button
          class="toggle-btn"
          :aria-label="'Open: ' + item.title"
          :class="state.openItems.includes(Number(index)) && 'is--open'"
          :aria-expanded="state.openItems.includes(Number(index))"
          :aria-controls="'content-' + index"
        />
      </div>

      <div
        v-if="item.text?.blocks"
        :id="'content-' + index"
        class="fold-out-block-item-content"
        role="region"
        :aria-hidden="!state.openItems.includes(Number(index))"
        :aria-labelledby="'heading-' + index"
        aria-live="polite"
      >
        <div class="fold-out-block-item-inner-content">
          <Alinea :blocks="item.text?.blocks" />
        </div>
      </div>
    </div>
  </article>
</template>

<script setup lang="ts">
import type { ICmsContentFoldOutBlocks } from '~~/types/_cms-types';
import type { IAnalytics, ITextBlock } from '~~/types/local_types';

const route = useRoute();

interface IProps extends ICmsContentFoldOutBlocks {
  blocks: Array<{
    title: string;
    text?: { blocks: Array<ITextBlock> };
  }>;
  singleOpen?: boolean;
}

const props = withDefaults(defineProps<IProps>(), {
  singleOpen: false,
});

const state = reactive({
  blocks: props.blocks,
  openItems: [] as number[],
});

// Toggle FoldOutBlock state
const toggleItem = async (index: number) => {
  const analyticsDefaults: IAnalytics = {
    linkAnalyticsTag: 'fold-out-block',
    linkUrl: route.fullPath,
    event: 'click_internal',
  };

  let linkText = '';

  if (state.openItems.includes(index)) {
    // Close the current section
    linkText = 'Blok Sluiten: ' + state.blocks[index]?.title + '';
    state.openItems = state.openItems.filter((i) => i !== index);
  } else {
    if (props.singleOpen) {
      // If only one panel can be open, replace the open item
      state.openItems = [index];
      linkText = 'Blok Openen: ' + state.blocks[index]?.title + '';
    } else {
      // Otherwise, allow multiples
      state.openItems.push(index);
      linkText = 'Blok Openen: ' + state.blocks[index]?.title + '';
    }
  }

  gtmTrackEvent({ ...analyticsDefaults, linkText });
};
</script>

<style lang="postcss" scoped>
.fold-out-block {
  display: grid;
}

.fold-out-block-item {
  --fold-out-block-item-border: 1px solid hsl(var(--color-primary-100));
  --fold-out-block-item-before-width: var(--unit-2);

  position: relative;
  display: grid;
  grid-template-rows: auto 0fr; /* Header takes 'auto', content collapses to 0 */
  transition: grid-template-rows 350ms ease;
  overflow: hidden; /* Prevent content overflow in collapsed state */
  border-bottom: var(--fold-out-block-item-border);
  border-right: var(--fold-out-block-item-border);

  &:first-of-type {
    border-top: var(--fold-out-block-item-border);
  }

  &:before {
    content: '';
    position: absolute;
    left: 0;
    height: 100%;
    background-color: hsl(var(--color-secondary-500));
    width: var(--fold-out-block-item-before-width);
  }

  &.open {
    grid-template-rows: auto 1fr; /* Header stays 'auto', content grows */
  }

  @media (--lg-viewport) {
    --fold-out-block-item-border: 2px solid hsl(var(--color-primary-200));
    --fold-out-block-item-before-width: var(--unit-4);
  }
}

.fold-out-block-item-heading {
  @add-mixin heading-md-italic;
  padding-right: calc(var(--unit-32) + var(--unit-6));
  color: hsl(var(--color-primary-900));
}

.fold-out-block-item-header {
  --header-padding: var(--unit-16) var(--unit-18);
  --header-open-padding: var(--unit-16) var(--unit-18) var(--unit-6);

  cursor: pointer;
  display: flex;
  justify-content: space-between;
  position: relative;
  padding: var(--header-padding);
  transition: padding 350ms ease;

  @media (--md-viewport) {
    --header-padding: var(--unit-16) var(--unit-24);
  }

  @media (--lg-viewport) {
    --header-padding: var(--unit-32) var(--unit-32);
  }

  &.is--open {
    --header-padding: var(--header-open-padding);

    @media (--md-viewport) {
      --header-open-padding: var(--unit-16) var(--unit-24) var(--unit-6);
    }

    @media (--lg-viewport) {
      --header-open-padding: var(--unit-32) var(--unit-32) var(--unit-16);
    }
  }
}

.fold-out-block-item-content {
  overflow: hidden;
}

.fold-out-block-item-inner-content {
  @add-mixin body-md;
  --inner-content-padding: 0 var(--unit-18) var(--unit-16);

  padding: var(--inner-content-padding);

  @media (--md-viewport) {
    --inner-content-padding: 0 var(--unit-24) var(--unit-16);
  }

  @media (--lg-viewport) {
    --inner-content-padding: 0 var(--unit-32) var(--unit-32);
  }
}

.toggle-btn {
  --button-size: var(--unit-24);
  --button-icon-height: var(--unit-2);
  --button-icon-width: var(--unit-12);
  --button-position-top: var(--unit-16);
  --button-position-right: var(--unit-18);
  --button-border: 1px solid hsl(var(--color-primary-200));

  cursor: pointer;

  display: flex;
  width: var(--button-size);
  height: var(--button-size);
  padding: var(--unit-6);
  justify-content: center;
  align-items: center;
  border-radius: 4px;
  border: var(--button-border);
  background: none;
  right: var(--button-position-right);
  top: var(--button-position-top);
  color: hsl(var(--color-secondary-500));

  margin: 0;
  position: absolute;
  transform-origin: center;
  transition: transform 350ms ease;

  @add-mixin focus light;

  &::after,
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    translate: -50% -50%;
    background-color: currentColor;
    border-radius: 2px;
  }

  &::before {
    height: var(--button-icon-height);
    width: var(--button-icon-width);
  }
  &::after {
    height: var(--button-icon-width);
    width: var(--button-icon-height);
    transition: scale 350ms ease;
    transform-origin: center center;
  }

  &.is--open {
    &::after {
      scale: 1 0;
    }
  }

  @media (--md-viewport) {
    --button-position-top: var(--unit-16);
    --button-position-right: var(--unit-24);
  }

  @media (--lg-viewport) {
    --button-size: var(--unit-34);
    --button-icon-height: var(--unit-2);
    --button-icon-width: var(--unit-16);
    --button-position-top: var(--unit-30);
    --button-position-right: var(--unit-32);
  }
}
</style>
